import React, { useEffect, useState, useRef } from 'react';
import { Box, Card, CardContent, TextField, Typography, Button, Stack, Snackbar, Alert, Divider, Grid, Switch, FormControlLabel, CardHeader } from '@mui/material';
import { getBranding as apiGetBranding, updateBranding as apiUpdateBranding } from '../services/settings';
import api from '../services/api';

const AdminBrandingPage = () => {
  const [form, setForm] = useState({
    companyName: '',
    clientLogo: '',
    pdfLogo: '',
    pdfLogoTenes: '',
    pdfHeader: { city: '', dateLabel: '' },
    pdfHeaderTenes: { city: '', dateLabel: '' },
    leaveAccepted: { pvBullet: '', regulatoryBullet: '' },
    countWeekends: false,
    countHolidays: false,
  });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const autoSaveRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiGetBranding();
      const data = res.data || {};
      setForm({
        companyName: data.companyName || '',
        clientLogo: data.clientLogo || '',
        pdfLogo: data.pdfLogo || '',
        pdfLogoTenes: data.pdfLogoTenes || '',
        pdfHeader: { city: data?.pdfHeader?.city || '', dateLabel: data?.pdfHeader?.dateLabel || '' },
        pdfHeaderTenes: { city: data?.pdfHeaderTenes?.city || '', dateLabel: data?.pdfHeaderTenes?.dateLabel || '' },
        leaveAccepted: { pvBullet: data?.leaveAccepted?.pvBullet || '', regulatoryBullet: data?.leaveAccepted?.regulatoryBullet || '' },
        countWeekends: !!data.countWeekends,
        countHolidays: !!data.countHolidays,
      });
    } catch (e) {
      setSnack({ open: true, message: "Impossible de charger le branding", severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };
  const onHeaderChange = (field, value) => {
    setForm((f) => ({ ...f, pdfHeader: { ...(f.pdfHeader || {}), [field]: value } }));
  };
  const onHeaderTenesChange = (field, value) => {
    setForm((f) => ({ ...f, pdfHeaderTenes: { ...(f.pdfHeaderTenes || {}), [field]: value } }));
  };
  const onLeaveAcceptedTplChange = (field, value) => {
    setForm((f) => ({ ...f, leaveAccepted: { ...(f.leaveAccepted || {}), [field]: value } }));
  };

  // Auto-save leaveAccepted templates with debounce so changes apply immediately
  useEffect(() => {
    if (!form.leaveAccepted) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      try {
        await apiUpdateBranding(form);
        setSnack({ open: true, message: 'Modèles enregistrés', severity: 'success' });
      } catch (e) {
        setSnack({ open: true, message: "Échec de l'enregistrement des modèles", severity: 'error' });
      }
    }, 600);
    return () => {
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    };
  }, [form.leaveAccepted]);

  const uploadImg = async (file) => {
    if (!file) return '';
    const fd = new FormData();
    fd.append('files', file);
    const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data.filePaths.split(',')[0]; // backend returns comma-separated paths, take first one
  };

  const handleUpload = async (targetKey) => {
    try {
      const input = document.getElementById(`file-${targetKey}`);
      if (!input || !input.files[0]) return;
      const url = await uploadImg(input.files[0]);
      if (targetKey === 'clientLogo') onChange('clientLogo', url);
      if (targetKey === 'pdfLogo') onChange('pdfLogo', url);
      if (targetKey === 'pdfLogoTenes') onChange('pdfLogoTenes', url);
      setSnack({ open: true, message: 'Image importée', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: "Import d'image échoué", severity: 'error' });
    }
  };

  const save = async () => {
    try {
      setLoading(true);
      await apiUpdateBranding(form);
      setSnack({ open: true, message: 'Branding enregistré', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: "Échec de l'enregistrement", severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Paramètres</Typography>
      <Card>
        <CardHeader title="Identité" subheader="Nom de l'entreprise et logos" />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Nom de l'entreprise" value={form.companyName} onChange={(e) => onChange('companyName', e.target.value)} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Affiché dans l'en-tête et/ou documents.</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>Modèles de texte - Titre de congé</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Placeholders: {'{pvNumber} {pvDate} {pvPerson} {companyName} {exerciseLabel} {pvNumberOpt} {pvDateOpt} {pvPersonOpt} {exerciseLabelOpt}'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ligne PV (procès-verbal)"
                placeholder={"– Vu le procès-verbal de nomination{pvNumberOpt}{pvDateOpt}{pvPersonOpt} en qualité du président du Conseil d’Administration de la société {companyName}."}
                value={form.leaveAccepted.pvBullet}
                onChange={(e) => onLeaveAcceptedTplChange('pvBullet', e.target.value)}
                helperText="Écrivez la ligne complète. Elle sera appliquée immédiatement."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Ligne réglementaire"
                placeholder={"– Vu les dispositions règlementaires."}
                value={form.leaveAccepted.regulatoryBullet}
                onChange={(e) => onLeaveAcceptedTplChange('regulatoryBullet', e.target.value)}
                helperText="Écrivez la ligne complète. Elle sera appliquée immédiatement."
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" gutterBottom>Logo du tableau de bord</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="URL Logo Dashboard" value={form.clientLogo} onChange={(e) => onChange('clientLogo', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input type="file" id="file-clientLogo" accept="image/*" />
                <Button onClick={() => handleUpload('clientLogo')} disabled={loading}>Uploader</Button>
              </Stack>
            </Grid>
            {form.clientLogo && (
              <Grid item xs={12}>
                <Box sx={{ p: 1, border: '1px solid #eee', display: 'inline-block', borderRadius: 1 }}>
                  <img src={form.clientLogo} alt="client" style={{ maxHeight: 80 }} />
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>En-tête pour PDF</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="URL Logo PDF" value={form.pdfLogo} onChange={(e) => onChange('pdfLogo', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input type="file" id="file-pdfLogo" accept="image/*" />
                <Button onClick={() => handleUpload('pdfLogo')} disabled={loading}>Uploader</Button>
              </Stack>
            </Grid>
            {form.pdfLogo && (
              <Grid item xs={12}>
                <Box sx={{ p: 1, border: '1px solid #eee', display: 'inline-block', borderRadius: 1 }}>
                  <img src={form.pdfLogo} alt="pdf" style={{ maxHeight: 80 }} />
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>En-tête pour PDF - Site Tenes</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="URL Logo PDF - Tenes" value={form.pdfLogoTenes} onChange={(e) => onChange('pdfLogoTenes', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <input type="file" id="file-pdfLogoTenes" accept="image/*" />
                <Button onClick={() => handleUpload('pdfLogoTenes')} disabled={loading}>Uploader</Button>
              </Stack>
            </Grid>
            {form.pdfLogoTenes && (
              <Grid item xs={12}>
                <Box sx={{ p: 1, border: '1px solid #eee', display: 'inline-block', borderRadius: 1 }}>
                  <img src={form.pdfLogoTenes} alt="pdf-tenes" style={{ maxHeight: 80 }} />
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>En-tête des documents PDF</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Ville (en-tête PDF)" value={form.pdfHeader.city} onChange={(e) => onHeaderChange('city', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Libellé date (en-tête PDF)" value={form.pdfHeader.dateLabel} onChange={(e) => onHeaderChange('dateLabel', e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>En-tête des documents PDF - Site Tenes</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Ville (en-tête PDF - Tenes)" value={form.pdfHeaderTenes.city} onChange={(e) => onHeaderTenesChange('city', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Libellé date (en-tête PDF - Tenes)" value={form.pdfHeaderTenes.dateLabel} onChange={(e) => onHeaderTenesChange('dateLabel', e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>Calcul des congés</Typography>
          <Stack direction="row" spacing={4} alignItems="center">
            <FormControlLabel
              control={<Switch checked={!!form.countWeekends} onChange={(e) => onChange('countWeekends', e.target.checked)} />}
              label="Compter les week-ends"
            />
            <FormControlLabel
              control={<Switch checked={!!form.countHolidays} onChange={(e) => onChange('countHolidays', e.target.checked)} />}
              label="Compter les jours fériés"
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" onClick={save} disabled={loading}>Enregistrer</Button>
            <Button variant="outlined" onClick={load} disabled={loading}>Réinitialiser</Button>
          </Stack>
        </CardContent>
      </Card>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminBrandingPage;
